import React, { useRef, useEffect } from 'react';
import {
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom-v5-compat';
import PropTypes from 'prop-types';
import { focusNotificationAlert, setPageFocus } from '../../utils/page';

const SupplementalClaimUploadErrorAlert = ({ scFailedFiles, uploading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const shouldFocusOtherWaysRef = useRef(false);
  const headlineRef = useRef(null);

  // Identifies the batch of failures so focus moves again on a new set of files
  const failedFilesKey = (scFailedFiles || [])
    .map(file => `${file.fileName}|${file.fileType}`)
    .join(',');

  useEffect(() => {
    if (!uploading && failedFilesKey) {
      const frame = requestAnimationFrame(focusNotificationAlert);
      return () => cancelAnimationFrame(frame);
    }

    return undefined;
  }, [failedFilesKey, uploading]);

  // Focus the element only if the user clicked the link that triggered navigation
  useEffect(() => {
    const newFilesTabUrl = searchParams.get('tab') === 'files';
    const legacyFilesTabUrl = location.pathname.endsWith('/files');
    const isOnFilesPage = newFilesTabUrl || legacyFilesTabUrl;

    if (isOnFilesPage && shouldFocusOtherWaysRef.current) {
      setPageFocus('#other-ways-to-send');
      shouldFocusOtherWaysRef.current = false;
    }
  }, [location.pathname, searchParams]);

  if (uploading || !scFailedFiles || !scFailedFiles?.length) {
    return null;
  }

  const numberOfFiles = scFailedFiles.length;
  const singleFile = numberOfFiles === 1;

  const pluralAlertContent = {
    headline: `We couldn’t upload some of your files`,
    paragraph1: `We’re sorry. Some of your documents weren’t successfully submitted.`,
    paragraph2: `Try submitting these ${numberOfFiles} documents again:`,
    paragraph3: `You can also submit these files by mail or in person instead.`,
    linkText: `Learn how to submit these files by mail or in person instead`,
  };

  const singularAlertContent = {
    headline: `We couldn’t upload some of your files`,
    paragraph1: `We’re sorry. Some of your documents weren’t successfully submitted.`,
    paragraph2: `Try submitting this document again:`,
    paragraph3: `You can also submit this file by mail or in person instead.`,
    linkText: `Learn how to submit this file by mail or in person instead`,
  };

  let alertContent = pluralAlertContent;

  if (singleFile) {
    alertContent = singularAlertContent;
  }

  const handleLinkClick = e => {
    e.preventDefault();
    const legacyFilesTabUrl = location.pathname.endsWith('/files');
    const newFilesTabUrl = searchParams.get('tab') === 'files';
    const legacyTabSyntax = !searchParams.get('tab');

    // When cst_appeal_details_va_tabs is removed, we can slim this down
    // to only check searchParams as the URL will then only be `?tab=files`
    // instead of the legacy /files syntax
    const isOnFilesPage = legacyFilesTabUrl || newFilesTabUrl;

    shouldFocusOtherWaysRef.current = true;

    if (legacyTabSyntax) {
      // Relative to the appeals/:id route that renders this alert, not the app root
      navigate('files#other-ways-to-send');
    } else {
      navigate({ search: '?tab=files', hash: '#other-ways-to-send' });
    }

    if (isOnFilesPage) {
      setPageFocus('#other-ways-to-send');
      shouldFocusOtherWaysRef.current = false;
    }
  };

  return (
    <va-alert
      class="claims-alert"
      close-btn-aria-label="Close notification"
      status="error"
      visible
    >
      <h2 slot="headline" ref={headlineRef}>
        {alertContent.headline}
      </h2>
      <p className="vads-u-margin-top--0">{alertContent.paragraph1}</p>
      <p className="vads-u-margin-top--0">{alertContent.paragraph2}</p>
      {/* add explicit role=list to expose the <ul> as a list for Safari/VoiceOver */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className="usa-unstyled-list" role="list">
        {scFailedFiles.map((file, index) => (
          <li
            className={index ? 'vads-u-margin-top--1' : undefined}
            key={`${file.fileName}-${file.fileType}`}
          >
            <span className="vads-u-font-weight--bold">{file.fileName}</span>
            <br />
            {file.fileType && <span>{file.fileType}</span>}
          </li>
        ))}
      </ul>
      <p>{alertContent.paragraph3}</p>
      <va-link
        href="../files#other-ways-to-send"
        onClick={handleLinkClick}
        text={alertContent.linkText}
      />
    </va-alert>
  );
};

SupplementalClaimUploadErrorAlert.propTypes = {
  scFailedFiles: PropTypes.arrayOf(
    PropTypes.shape({ fileName: PropTypes.string, fileType: PropTypes.string }),
  ),
  uploading: PropTypes.bool,
};

export default SupplementalClaimUploadErrorAlert;
